/**
 * AdminFundingPage — V9.5
 *
 * Page d'administration centralisée pour le module Aides / Financements.
 *
 * Fonctions :
 *  - tableau filtrable de toutes les aides (tous statuts) ;
 *  - création manuelle (form complet 30+ champs) ;
 *  - **création assistée par IA** : bouton « Analyser l'aide avec l'IA »
 *    qui appelle POST /api/funding/extract-from-url et préremplit le form ;
 *  - actions par ligne : publier · dépublier · archiver · supprimer ·
 *    restaurer (via /admin/entities/funding_opportunity/...) · dupliquer ·
 *    épingler · mettre en avant · réordonner ;
 *  - aperçu source + niveau de confiance IA + champs incertains affichés ;
 *  - rien n'est publié automatiquement — l'admin valide toujours.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { adminMailto, getAdminEmail } from "@/lib/adminContact";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";

import {
  HandCoins, Sparkles, Plus, Pencil, Eye, EyeOff, Archive, Trash2, Copy,
  Pin, Star, ArrowUpDown, ExternalLink, Loader2, Save, ArrowLeft,
  AlertTriangle, ShieldAlert, RefreshCw, Globe,
} from "lucide-react";

// -------------------------------------------------------------------------
//  Référentiels
// -------------------------------------------------------------------------
const STATUSES = [
  { v: "draft",         label: "Brouillon",         tone: "outline" },
  { v: "submitted",     label: "Soumise",           tone: "secondary" },
  { v: "admin_review",  label: "À valider (COPF/Fédé)", tone: "secondary" },
  { v: "active",        label: "Actif",             tone: "default" },
  { v: "to_check",      label: "À vérifier",        tone: "secondary" },
  { v: "expired",       label: "Expiré",            tone: "secondary" },
  { v: "closed",        label: "Clôturé",           tone: "secondary" },
  { v: "archived",      label: "Archivé",           tone: "outline" },
];
const PROVIDER_TYPES = [
  { v: "pays", label: "Pays" }, { v: "djs", label: "DJS" },
  { v: "commune", label: "Commune" }, { v: "federation", label: "Fédération" },
  { v: "copf", label: "COPF" }, { v: "sponsor", label: "Sponsor" },
  { v: "prive", label: "Privé" }, { v: "association", label: "Association" },
  { v: "autre", label: "Autre" },
];
const ELIGIBLE_PROFILES = ["athlete", "club", "coach", "federation", "jeune", "haut_niveau", "association", "autre"];
const TERRITORIES = [
  { v: "polynesie_francaise", label: "Polynésie française" },
  { v: "commune", label: "Commune" }, { v: "ile", label: "Île" },
  { v: "national", label: "National" }, { v: "international", label: "International" },
];
const EXPENSE_CATEGORIES = ["transport", "hebergement", "inscription", "equipement", "formation", "preparation", "competition", "autre"];
const PERIODICITIES = [
  { v: "annuelle", label: "Annuelle" }, { v: "permanente", label: "Permanente" },
  { v: "ponctuelle", label: "Ponctuelle" }, { v: "appel_a_projets", label: "Appel à projets" },
  { v: "campagne", label: "Campagne" },
];
const RELIABILITY_LEVELS = [
  { v: "officielle", label: "Source officielle" },
  { v: "presse", label: "Source presse" },
  { v: "declaratif", label: "Déclaratif" },
  { v: "a_verifier", label: "À vérifier" },
];

const STATUS_LABEL = Object.fromEntries(STATUSES.map(s => [s.v, s.label]));
const STATUS_TONE = Object.fromEntries(STATUSES.map(s => [s.v, s.tone]));

const isoToInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
const inputToIso = (d) => (d ? new Date(d + "T00:00:00.000Z").toISOString() : null);

// -------------------------------------------------------------------------
//  Empty form template
// -------------------------------------------------------------------------
const emptyForm = () => ({
  title: "", provider_name: "", provider_type: "", device_name: "",
  short_description: "", long_description: "",
  eligible_profiles: [], disciplines: [], territory: "", island: "", commune: "",
  eligibility_criteria: "",
  amount_min: "", amount_max: "", amount_text: "", funding_rate: "",
  expense_covered: [], expense_excluded: [],
  opening_date: "", deadline_date: "", periodicity: "",
  application_url: "", official_url: "", contact_email: "", contact_phone: "", contact_name: "",
  required_documents: [],
  source_url: "", source_title: "", source_type: "", reliability_level: "",
  admin_notes: "", public_notes: "",
  status: "draft", published: false,
  ai_extracted: false, ai_confidence: null, ai_uncertain_fields: [],
});

// =========================================================================
//  PAGE
// =========================================================================
export default function AdminFundingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user && (user.roles || []).some(r => ["admin", "copf"].includes(r));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);

  // IA
  const [aiUrl, setAiUrl] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null); // {prefill, uncertain, confidence, warning}

  const fetchItems = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      // Vue admin : utilise le panneau modération pour récupérer TOUS les statuts
      const { data } = await api.get("/admin/moderation/list", {
        params: { entity: "funding_opportunity", limit: 200 },
      });
      setItems(data.items || []);
    } catch (e) { showError(e, { prefix: "Aides :" }); }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = useMemo(() => {
    let arr = items;
    if (statusFilter !== "all") arr = arr.filter(i => i.status === statusFilter || i.raw_status === statusFilter);
    if (q.trim()) {
      const s = q.toLowerCase();
      arr = arr.filter(i => (i.name || "").toLowerCase().includes(s));
    }
    return arr;
  }, [items, q, statusFilter]);

  // ---- AI extract ----
  const runAiExtract = async () => {
    if (!aiUrl.trim()) { toast.warning("Renseigne d'abord l'URL officielle de l'aide."); return; }
    setAiBusy(true); setAiResult(null);
    try {
      const { data } = await api.post("/funding/extract-from-url", { url: aiUrl.trim() });
      setAiResult(data);
      // Pré-remplit le form
      const p = data.prefill || {};
      setForm({
        ...emptyForm(),
        title: p.title || "",
        provider_name: p.provider_name || "",
        provider_type: p.provider_type || "",
        device_name: p.device_name || "",
        short_description: p.short_description || "",
        long_description: p.long_description || "",
        eligible_profiles: p.eligible_profiles || [],
        disciplines: p.disciplines || [],
        territory: p.territory || "",
        island: p.island || "",
        commune: p.commune || "",
        eligibility_criteria: p.eligibility_criteria || "",
        amount_min: p.amount_min ?? "",
        amount_max: p.amount_max ?? "",
        amount_text: p.amount_text || "",
        funding_rate: p.funding_rate || "",
        expense_covered: p.expense_covered || [],
        expense_excluded: p.expense_excluded || [],
        opening_date: p.opening_date ? p.opening_date.slice(0, 10) : "",
        deadline_date: p.deadline_date ? p.deadline_date.slice(0, 10) : "",
        periodicity: p.periodicity || "",
        application_url: p.application_url || "",
        official_url: p.official_url || "",
        contact_email: p.contact_email || "",
        contact_phone: p.contact_phone || "",
        contact_name: p.contact_name || "",
        required_documents: p.required_documents || [],
        source_url: p.source_url || aiUrl,
        source_title: p.source_title || "",
        source_type: p.source_type || "a_verifier",
        reliability_level: p.reliability_level || "a_verifier",
        admin_notes: "",
        status: "draft", published: false,
        ai_extracted: true,
        ai_confidence: p.ai_confidence,
        ai_uncertain_fields: p.ai_uncertain_fields || [],
      });
      setEditingId(null);
      setShowForm(true);
      toast.success(`Préremplissage IA terminé (confiance ${Math.round((p.ai_confidence || 0) * 100)} %).`);
    } catch (e) { showError(e, { prefix: "Extraction IA :" }); }
    finally { setAiBusy(false); }
  };

  // ---- Save (create or update) ----
  const save = async () => {
    if (!form.title.trim()) { toast.warning("Titre obligatoire."); return; }
    if (!form.provider_name.trim()) { toast.warning("Porteur obligatoire."); return; }
    const payload = {
      ...form,
      amount_min: form.amount_min === "" ? null : Number(form.amount_min),
      amount_max: form.amount_max === "" ? null : Number(form.amount_max),
      opening_date: form.opening_date ? inputToIso(form.opening_date) : null,
      deadline_date: form.deadline_date ? inputToIso(form.deadline_date) : null,
    };
    try {
      if (editingId) {
        await api.patch(`/funding/${editingId}`, payload);
        toast.success("Aide mise à jour.");
      } else {
        await api.post("/funding", payload);
        toast.success("Aide créée (brouillon).");
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm()); setAiResult(null); setAiUrl("");
      fetchItems();
    } catch (e) { showError(e, { prefix: "Enregistrement :" }); }
  };

  // ---- Actions ----
  const doAction = async (item, action) => {
    try {
      if (action === "publish") await api.post(`/funding/${item.id}/publish`, {});
      else if (action === "close") await api.post(`/funding/${item.id}/close`, {});
      else if (action === "duplicate") { await api.post(`/funding/${item.id}/duplicate`, {}); toast.success("Aide dupliquée en brouillon."); }
      else if (action === "pin")     await api.post(`/funding/${item.id}/toggle-flag`, { flag: "pinned",   value: !item.pinned });
      else if (action === "feature") await api.post(`/funding/${item.id}/toggle-flag`, { flag: "featured", value: !item.featured });
      else if (action === "archive") await api.post(`/admin/entities/funding_opportunity/${item.id}/archive`, { reason: "Archivé via /admin/funding" });
      else if (action === "unpublish") await api.post(`/admin/entities/funding_opportunity/${item.id}/unpublish`, { reason: "Dépubliée via /admin/funding" });
      else if (action === "restore") await api.post(`/admin/entities/funding_opportunity/${item.id}/restore`, {});
      else if (action === "delete") {
        const reason = window.prompt('Raison de la suppression définitive ? (obligatoire)\nTape "SUPPRIMER" pour confirmer dans le prochain prompt.');
        if (!reason) return;
        const c = window.prompt('Confirme en tapant exactement : SUPPRIMER');
        if (c !== "SUPPRIMER") { toast.warning("Confirmation incorrecte. Suppression annulée."); return; }
        await api.delete(`/admin/entities/funding_opportunity/${item.id}`, { params: { confirm: "SUPPRIMER", reason } });
        toast.success("Aide supprimée définitivement.");
      }
      fetchItems();
    } catch (e) { showError(e, { prefix: `Action ${action} :` }); }
  };

  const openEdit = async (item) => {
    try {
      const { data } = await api.get(`/funding/${item.slug || item.id}`);
      setForm({
        ...emptyForm(),
        ...data,
        opening_date: data.opening_date ? data.opening_date.slice(0, 10) : "",
        deadline_date: (data.deadline_date || data.deadline) ? (data.deadline_date || data.deadline).slice(0, 10) : "",
        amount_min: data.amount_min ?? "",
        amount_max: data.amount_max ?? "",
        eligible_profiles: data.eligible_profiles || [],
        disciplines: data.disciplines || [],
        expense_covered: data.expense_covered || [],
        expense_excluded: data.expense_excluded || [],
        required_documents: data.required_documents || [],
        ai_uncertain_fields: data.ai_uncertain_fields || [],
      });
      setEditingId(item.id);
      setAiResult(null);
      setShowForm(true);
    } catch (e) { showError(e, { prefix: "Chargement :" }); }
  };

  // ---- Permissions ----
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl p-8" data-testid="funding-admin-forbidden">
        <Card className="p-6">
          <ShieldAlert className="h-6 w-6 text-[hsl(var(--aito-energy))]" />
          <h2 className="font-display text-xl font-bold mt-2">Accès administrateur</h2>
          <p className="text-sm text-muted-foreground mt-1">
                    Ton compte n'a pas les droits admin/COPF. Contacte <a href={adminMailto({ subject: "Demande d'accès Admin/COPF" })} className="underline">{getAdminEmail()}</a>.
          </p>
        </Card>
      </div>
    );
  }

  // ---- Render ----
  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-6 space-y-4" data-testid="admin-funding-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => nav("/admin")} data-testid="funding-back-admin">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />Admin
            </Button>
            <span>/ Aides / Financements</span>
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2 mt-1">
            <HandCoins className="h-7 w-7 text-[hsl(var(--aito-lagoon))]" />
            Aides aux sportifs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Recense, structure et publie les dispositifs (subventions, bourses, sponsoring, appels à projets) ouverts en Polynésie française.
            Toutes les actions sont auditées dans <code>audit_logs</code>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchItems} disabled={loading} data-testid="funding-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Rafraîchir
          </Button>
          <Button onClick={() => { setForm(emptyForm()); setEditingId(null); setAiResult(null); setShowForm(true); }} data-testid="funding-new">
            <Plus className="h-4 w-4 mr-1.5" />Nouvelle aide
          </Button>
        </div>
      </div>

      {/* Carte IA */}
      <Card className="p-4 border-[hsl(var(--aito-lagoon))]/30 bg-gradient-to-br from-[hsl(var(--aito-lagoon))]/5 to-transparent" data-testid="funding-ai-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-[hsl(var(--aito-lagoon))]/10 flex items-center justify-center text-[hsl(var(--aito-lagoon))] shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold">Analyser l'aide avec l'IA</h3>
            <p className="text-xs text-muted-foreground mb-2">
              Colle l'URL d'une page officielle (DJS, Pays, COPF, fédération, communiqué PDF...). L'IA extrait les champs, cite la source, signale les incertitudes — <strong>tu valides et publies manuellement</strong>.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Input
                value={aiUrl}
                onChange={(e) => setAiUrl(e.target.value)}
                placeholder="https://djs.gov.pf/dispositif-..."
                className="flex-1 min-w-[280px]"
                data-testid="funding-ai-url"
              />
              <Button onClick={runAiExtract} disabled={aiBusy || !aiUrl.trim()} data-testid="funding-ai-run">
                {aiBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                Analyser
              </Button>
            </div>
            {aiResult && (
              <div className="mt-3 rounded-md border bg-card p-3 text-xs space-y-1" data-testid="funding-ai-result">
                <div className="flex items-center gap-2">
                  <Badge variant="default">IA</Badge>
                  <span className="text-muted-foreground">Confiance : <strong>{Math.round((aiResult.ai_confidence || 0) * 100)} %</strong></span>
                  <span className="text-muted-foreground">·</span>
                  <a href={aiResult.source_url} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--aito-lagoon))] hover:underline truncate max-w-[40ch] inline-flex items-center gap-1">
                    {aiResult.source_title || aiResult.source_url}<Globe className="h-3 w-3" />
                  </a>
                </div>
                {(aiResult.ai_uncertain_fields || []).length > 0 && (
                  <div className="flex items-start gap-1.5 pt-1">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                    <span className="text-amber-700">Champs incertains : {aiResult.ai_uncertain_fields.join(", ")}</span>
                  </div>
                )}
                <p className="text-muted-foreground pt-1">⚠️ {aiResult.warning}</p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Filtres + table */}
      <Card className="p-4 space-y-3">
        <div className="flex gap-2 flex-wrap items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Recherche</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="titre, porteur..." data-testid="funding-filter-q" />
          </div>
          <div className="w-44">
            <Label className="text-xs">Statut</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="funding-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-testid="funding-table">
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-2 pr-2 w-8"></th>
                <th className="py-2 pr-2">Titre / porteur</th>
                <th className="py-2 pr-2">Statut</th>
                <th className="py-2 pr-2">Deadline</th>
                <th className="py-2 pr-2">Modif. par</th>
                <th className="py-2 pr-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}><td colSpan={6} className="py-2"><Skeleton className="h-6 w-full" /></td></tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="funding-empty">
                  Aucune aide ne correspond à ces filtres.
                </td></tr>
              )}
              {!loading && filtered.map((it) => (
                <tr key={it.id} className="border-b hover:bg-muted/30" data-testid={`funding-row-${it.id}`}>
                  <td className="py-2 pr-2">
                    <div className="flex flex-col gap-0.5">
                      {it.pinned && <Pin className="h-3 w-3 text-[hsl(var(--aito-energy))]" />}
                      {it.featured && <Star className="h-3 w-3 text-[hsl(var(--aito-lagoon))]" />}
                    </div>
                  </td>
                  <td className="py-2 pr-2">
                    <div className="font-medium truncate max-w-[40ch]">{it.name}</div>
                    {it.slug && <div className="text-[10px] text-muted-foreground">{it.slug}</div>}
                  </td>
                  <td className="py-2 pr-2">
                    <Badge variant={STATUS_TONE[it.status] || "secondary"} className="text-[10px]">
                      {STATUS_LABEL[it.status] || it.status}
                    </Badge>
                  </td>
                  <td className="py-2 pr-2 text-xs whitespace-nowrap">
                    {it.deadline_date ? new Date(it.deadline_date).toLocaleDateString("fr-FR") : (it.deadline ? new Date(it.deadline).toLocaleDateString("fr-FR") : "—")}
                  </td>
                  <td className="py-2 pr-2 text-xs">
                    {it.owner_email ? <span className="truncate max-w-[16ch] inline-block" title={it.owner_email}>{it.owner_email}</span> : "—"}
                  </td>
                  <td className="py-2 pr-2 text-right">
                    <div className="inline-flex gap-0.5">
                      <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => openEdit(it)} title="Modifier" data-testid={`funding-edit-${it.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {it.status === "draft" || it.status === "unpublished" || it.status === "archived" || it.status === "deleted" ? (
                        it.status === "archived" || it.status === "deleted" || it.status === "unpublished" ? (
                          <Button size="sm" variant="ghost" className="h-7 px-1.5 text-emerald-600" onClick={() => doAction(it, "restore")} title="Restaurer" data-testid={`funding-restore-${it.id}`}>
                            <ArrowUpDown className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => doAction(it, "publish")} title="Publier" data-testid={`funding-publish-${it.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )
                      ) : (
                        <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => doAction(it, "unpublish")} title="Dépublier" data-testid={`funding-unpublish-${it.id}`}>
                          <EyeOff className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => doAction(it, "pin")} title={it.pinned ? "Désépingler" : "Épingler"} data-testid={`funding-pin-${it.id}`}>
                        <Pin className={`h-3.5 w-3.5 ${it.pinned ? "text-[hsl(var(--aito-energy))]" : ""}`} />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => doAction(it, "feature")} title={it.featured ? "Retirer la mise en avant" : "Mettre en avant"} data-testid={`funding-feature-${it.id}`}>
                        <Star className={`h-3.5 w-3.5 ${it.featured ? "fill-[hsl(var(--aito-lagoon))] text-[hsl(var(--aito-lagoon))]" : ""}`} />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => doAction(it, "duplicate")} title="Dupliquer" data-testid={`funding-duplicate-${it.id}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => doAction(it, "archive")} title="Archiver" data-testid={`funding-archive-${it.id}`}>
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-1.5 text-destructive" onClick={() => doAction(it, "delete")} title="Supprimer définitivement" data-testid={`funding-delete-${it.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditingId(null); setForm(emptyForm()); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="funding-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifier l'aide" : "Nouvelle aide"}</DialogTitle>
            <DialogDescription>
              Tous les champs sont éditables. {form.ai_extracted && <Badge variant="secondary" className="ml-2 text-[10px]">Préremplie par IA</Badge>}
            </DialogDescription>
          </DialogHeader>
          <FundingForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }} data-testid="funding-form-cancel">Annuler</Button>
            <Button onClick={save} data-testid="funding-form-save"><Save className="h-4 w-4 mr-1.5" />{editingId ? "Enregistrer" : "Créer en brouillon"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================================
//  FORM (tous les champs V9.5)
// =========================================================================
function FundingForm({ form, setForm }) {
  const u = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const e = (k) => (ev) => setForm(f => ({ ...f, [k]: ev.target.value }));
  const toggle = (key, val) => setForm(f => {
    const arr = new Set(f[key] || []);
    if (arr.has(val)) arr.delete(val); else arr.add(val);
    return { ...f, [key]: Array.from(arr) };
  });

  return (
    <Tabs defaultValue="essentiel" className="w-full">
      <TabsList className="grid grid-cols-5 w-full" data-testid="funding-form-tabs">
        <TabsTrigger value="essentiel">Essentiel</TabsTrigger>
        <TabsTrigger value="benef">Bénéficiaires</TabsTrigger>
        <TabsTrigger value="montant">Montant</TabsTrigger>
        <TabsTrigger value="depot">Dépôt</TabsTrigger>
        <TabsTrigger value="source">Source & affichage</TabsTrigger>
      </TabsList>

      <TabsContent value="essentiel" className="space-y-3 pt-3">
        <Field label="Titre *"><Input value={form.title} onChange={e("title")} data-testid="funding-form-title" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Porteur (provider_name) *"><Input value={form.provider_name} onChange={e("provider_name")} data-testid="funding-form-provider" /></Field>
          <Field label="Type de porteur">
            <Select value={form.provider_type || ""} onValueChange={u("provider_type")}>
              <SelectTrigger data-testid="funding-form-provider-type"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{PROVIDER_TYPES.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Nom du dispositif (si différent)"><Input value={form.device_name} onChange={e("device_name")} /></Field>
        <Field label="Description courte"><Input value={form.short_description} onChange={e("short_description")} data-testid="funding-form-short-desc" /></Field>
        <Field label="Description détaillée"><Textarea rows={5} value={form.long_description} onChange={e("long_description")} data-testid="funding-form-long-desc" /></Field>
      </TabsContent>

      <TabsContent value="benef" className="space-y-3 pt-3">
        <Field label="Profils éligibles">
          <div className="flex flex-wrap gap-1.5">
            {ELIGIBLE_PROFILES.map(p => (
              <button key={p} type="button" onClick={() => toggle("eligible_profiles", p)}
                className={`px-2 py-1 rounded-md text-xs border ${(form.eligible_profiles||[]).includes(p) ? "border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/10" : "border-border"}`}
                data-testid={`funding-form-elig-${p}`}>
                {p}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Disciplines (séparées par virgule)">
          <Input value={(form.disciplines || []).join(", ")} onChange={(ev) => u("disciplines")(ev.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="surf, va-a, jiu-jitsu-bresilien" />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Territoire">
            <Select value={form.territory || ""} onValueChange={u("territory")}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{TERRITORIES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Île"><Input value={form.island} onChange={e("island")} placeholder="Tahiti, Moorea..." /></Field>
          <Field label="Commune"><Input value={form.commune} onChange={e("commune")} placeholder="Papeete..." /></Field>
        </div>
        <Field label="Critères d'éligibilité"><Textarea rows={4} value={form.eligibility_criteria} onChange={e("eligibility_criteria")} /></Field>
      </TabsContent>

      <TabsContent value="montant" className="space-y-3 pt-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Montant min (XPF)"><Input type="number" value={form.amount_min} onChange={e("amount_min")} placeholder="50000" /></Field>
          <Field label="Montant max (XPF)"><Input type="number" value={form.amount_max} onChange={e("amount_max")} placeholder="500000" /></Field>
        </div>
        <Field label="Montant texte (si non chiffrable)"><Input value={form.amount_text} onChange={e("amount_text")} placeholder='ex: "Variable selon dossier"' /></Field>
        <Field label="Taux de prise en charge"><Input value={form.funding_rate} onChange={e("funding_rate")} placeholder="ex: 70% plafonné à 1M XPF" /></Field>
        <Field label="Dépenses couvertes">
          <div className="flex flex-wrap gap-1.5">
            {EXPENSE_CATEGORIES.map(c => (
              <button key={c} type="button" onClick={() => toggle("expense_covered", c)}
                className={`px-2 py-1 rounded-md text-xs border ${(form.expense_covered||[]).includes(c) ? "border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/10" : "border-border"}`}>
                {c}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Dépenses exclues (texte libre, séparées par virgule)">
          <Input value={(form.expense_excluded || []).join(", ")} onChange={(ev) => u("expense_excluded")(ev.target.value.split(",").map(s => s.trim()).filter(Boolean))} />
        </Field>
      </TabsContent>

      <TabsContent value="depot" className="space-y-3 pt-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date d'ouverture"><Input type="date" value={form.opening_date} onChange={e("opening_date")} /></Field>
          <Field label="Date limite de dépôt"><Input type="date" value={form.deadline_date} onChange={e("deadline_date")} data-testid="funding-form-deadline" /></Field>
        </div>
        <Field label="Périodicité">
          <Select value={form.periodicity || ""} onValueChange={u("periodicity")}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{PERIODICITIES.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Lien candidature (application_url)"><Input value={form.application_url} onChange={e("application_url")} placeholder="https://..." /></Field>
        <Field label="Lien officiel (official_url)"><Input value={form.official_url} onChange={e("official_url")} placeholder="https://..." /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Contact email"><Input type="email" value={form.contact_email} onChange={e("contact_email")} /></Field>
          <Field label="Contact tél."><Input value={form.contact_phone} onChange={e("contact_phone")} /></Field>
          <Field label="Contact nom"><Input value={form.contact_name} onChange={e("contact_name")} /></Field>
        </div>
        <Field label="Documents à fournir (séparés par virgule)">
          <Input value={(form.required_documents || []).join(", ")} onChange={(ev) => u("required_documents")(ev.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="RIB, CV sportif, palmares..." />
        </Field>
      </TabsContent>

      <TabsContent value="source" className="space-y-3 pt-3">
        <Field label="URL source (officielle)"><Input value={form.source_url} onChange={e("source_url")} /></Field>
        <Field label="Titre de la source"><Input value={form.source_title} onChange={e("source_title")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Niveau de fiabilité">
            <Select value={form.reliability_level || ""} onValueChange={u("reliability_level")}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{RELIABILITY_LEVELS.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Statut">
            <Select value={form.status} onValueChange={u("status")}>
              <SelectTrigger data-testid="funding-form-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.filter(s => s.v !== "archived").map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Notes admin (privées)"><Textarea rows={2} value={form.admin_notes} onChange={e("admin_notes")} /></Field>
        <Field label="Notes publiques (affichées sur la fiche)"><Textarea rows={2} value={form.public_notes} onChange={e("public_notes")} /></Field>
        {form.ai_extracted && (form.ai_uncertain_fields || []).length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-700 mt-0.5 shrink-0" />
            <span className="text-amber-800">
              <strong>Champs incertains relevés par l'IA :</strong> {form.ai_uncertain_fields.join(", ")}.
              Merci de vérifier ces champs avant publication.
            </span>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
